export type Permission = string;

export interface PermissionDefinition {
  resource: string;
  subresource?: string;
  action: string;
  description: string;
}

/**
 * Parses a `resource[.subresource]:action` permission code into parts.
 * @param {Permission} permissionCode - Canonical permission string
 * @returns {{ resource: string; subresource?: string; action: string }} Parsed segments
 */
function parsePermissionCode(permissionCode: Permission): {
  resource: string;
  subresource?: string;
  action: string;
} {
  const [left, action] = permissionCode.split(':');
  if (!left || !action) {
    throw new Error(`Invalid permission code format: ${permissionCode}`);
  }

  const [resource, ...rest] = left.split('.');
  if (!resource) {
    throw new Error(`Invalid permission code resource: ${permissionCode}`);
  }
  const subresource = rest.length > 0 ? rest.join('.') : undefined;

  return { resource, subresource, action };
}

/**
 * Serializes a permission definition back to the canonical code string.
 * @param {Pick<PermissionDefinition, 'resource' | 'subresource' | 'action'>} permission
 *   Parts to join
 * @returns {Permission} Canonical code
 */
export function formatPermission(
  permission: Pick<PermissionDefinition, 'resource' | 'subresource' | 'action'>,
): Permission {
  const left = permission.subresource
    ? `${permission.resource}.${permission.subresource}`
    : permission.resource;
  return `${left}:${permission.action}`;
}

/**
 * Builds a registry entry from a permission code and description.
 * @param {Permission} permissionCode - Canonical permission string
 * @param {string} description - Human-readable description
 * @returns {PermissionDefinition} Structured definition including description
 */
function definePermission(permissionCode: Permission, description: string): PermissionDefinition {
  const parsed = parsePermissionCode(permissionCode);
  return { ...parsed, description };
}

/**
 * Canonical permission registry (system-defined).
 *
 * Matches PRD-003 permission registry tables and is used for:
 * - permission claim enumeration (read-only)
 * - server-side authorization checks
 */
export const PermissionRegistry = [
  // Patient
  definePermission('patients:read', 'View patient profiles and history'),
  definePermission('patients:create', 'Register new patients'),
  definePermission('patients:update', 'Edit patient information'),
  definePermission('patients:delete', 'Soft-delete patient records (admin only)'),
  definePermission('patients:merge', 'Merge duplicate patient records (admin only)'),

  // Clinical
  definePermission('charts:read', 'View dental charts'),
  definePermission('charts:write', 'Create and update dental charts'),
  definePermission('treatment_plans:read', 'View treatment plans'),
  definePermission('treatment_plans:write', 'Create and update treatment plans'),
  definePermission('treatment_plans:approve', 'Approve and finalize treatment plans'),
  definePermission('prescriptions:write', 'Issue prescriptions'),
  definePermission('lab_orders:read', 'View lab orders and results'),
  definePermission('lab_orders:write', 'Create and update lab orders'),
  definePermission('procedure-catalog:read', 'View organization treatment procedure catalog'),
  definePermission('procedure-catalog:manage', 'Create and manage procedure categories'),
  definePermission('procedure-catalog:update', 'Update and deactivate catalog items and steps'),
  definePermission('patient-procedures:read', 'View multi-step patient treatment procedures'),
  definePermission('patient-procedures:write', 'Start, complete, skip, or redo procedure steps'),

  // Appointments & Queue
  definePermission('appointments:read', 'View appointments'),
  definePermission('appointments:create', 'Book new appointments'),
  definePermission('appointments:update', 'Reschedule or modify appointments'),
  definePermission('appointments:cancel', 'Cancel appointments'),
  definePermission('queue:manage', 'Manage clinic queue — check-in, call next, mark done'),

  // Billing
  definePermission('invoices:read', 'View invoices'),
  definePermission('invoices:create', 'Create invoices'),
  definePermission('invoices:void', 'Void invoices'),
  definePermission('payments:record', 'Record payments (cash, UPI, card)'),
  definePermission('payments:refund', 'Issue refunds'),
  definePermission('reports:financial', 'View financial reports and GST summaries'),

  // Staff & Users
  definePermission('users:read', 'View user list and profiles'),
  definePermission('users:invite', 'Send user invitations'),
  definePermission('users:update', 'Update user roles and info'),
  definePermission('users:deactivate', 'Deactivate users'),
  definePermission('roles:read', 'View roles and permissions'),
  definePermission('roles:manage', 'Create, update, delete custom roles'),
  definePermission('clinic_assignments:manage', 'Assign/unassign users to clinics'),

  // Clinic Settings
  definePermission('clinic.settings:read', 'View clinic configuration'),
  definePermission('clinic.settings:write', 'Update clinic configuration'),
  definePermission('clinic:create', 'Create new clinic locations'),
  definePermission('clinic:deactivate', 'Deactivate a clinic'),

  // Organization
  definePermission('org.settings:manage', 'Manage org-level settings and branding'),
  definePermission('org.billing:manage', 'Manage subscription and billing'),
  definePermission('org.integrations:manage', 'Configure third-party integrations'),
  definePermission('org.audit_logs:read', 'View audit logs'),

  // Platform support (Innexe operators only — not assignable by org admins)
  definePermission('platform.support:access', 'Enter platform support mode for a customer org'),
  definePermission(
    'platform.support:manage',
    'Use elevated support tools (quota overrides, diagnostics)',
  ),
] as const satisfies readonly PermissionDefinition[];
